import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Wand2 } from "lucide-react";
import { startGoogleSignIn } from "./auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoSection({ user, authChecked }) {
  const [product, setProduct] = useState("");
  const [audience, setAudience] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [model, setModel] = useState("gpt");

  const generate = async () => {
    if (!product.trim() || !audience.trim() || loading) return;
    setLoading(true);
    setOutput("");
    setError("");
    try {
      const res = await fetch(`${API}/demo-outreach`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product, audience, model }),
      });
      if (!res.ok) throw new Error("Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let text = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        text += decoder.decode(value, { stream: true });
        setOutput(text);
      }
    } catch {
      setError("Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-28 md:py-32" data-testid="demo-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-1/3 h-[380px] w-[380px] rounded-full bg-teal-600/10 blur-[130px]"
      />
      <div className="relative mx-auto max-w-3xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 text-center"
        >
          <p className="mb-4 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-teal-300">
            <Sparkles className="h-3.5 w-3.5" />
            Live Demo
          </p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Watch AI write
            <br />
            your outreach.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-400 md:text-lg">
            Tell us what you sell and who you sell to — our AI drafts a
            personalized message in seconds.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl md:p-8"
        >
          {authChecked && !user ? (
            <div className="flex flex-col items-center gap-5 py-10 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                <Lock className="h-6 w-6 text-teal-300" />
              </span>
              <div>
                <p className="font-display text-lg font-semibold text-white">
                  This demo is for members
                </p>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-400">
                  Sign in with Google to watch AI write your outreach — free,
                  no credit card required.
                </p>
              </div>
              <button
                onClick={startGoogleSignIn}
                className="flex items-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 px-7 py-3 text-sm font-semibold text-white shadow-[0_0_32px_rgba(45,212,191,0.3)] transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_0_44px_rgba(45,212,191,0.45)]"
                data-testid="demo-signin-button"
              >
                Sign in with Google
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                What do you sell?
              </label>
              <input
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder="e.g. Shopify inventory software"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-300 focus:border-teal-400/50"
                data-testid="demo-product-input"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Who is it for?
              </label>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="e.g. D2C e-commerce founders"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-300 focus:border-teal-400/50"
                data-testid="demo-audience-input"
              />
            </div>
          </div>

          <div
            className="mt-6 flex items-center justify-center gap-2"
            data-testid="demo-model-toggle"
          >
            {[
              { id: "gpt", label: "GPT 5.4 Mini" },
              { id: "gemini", label: "Gemini 3 Flash" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setModel(m.id)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-[background-color,color,transform] duration-300 ${
                  model === m.id
                    ? "bg-gradient-to-r from-blue-600 to-teal-500 text-white shadow-[0_0_18px_rgba(45,212,191,0.35)]"
                    : "border border-white/10 bg-white/5 text-slate-400 hover:text-white"
                }`}
                data-testid={`demo-model-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <button
            onClick={generate}
            disabled={loading || !product.trim() || !audience.trim()}
            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 py-3.5 text-sm font-semibold text-white shadow-[0_0_32px_rgba(45,212,191,0.3)] transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.01] hover:shadow-[0_0_44px_rgba(45,212,191,0.45)] disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="demo-generate-button"
          >
            <Wand2 className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Writing your message…" : "Generate Message"}
          </button>

          <div
            className="mt-6 min-h-[120px] rounded-xl border border-white/5 bg-[#070818]/80 p-5"
            data-testid="demo-output"
          >
            {error && <p className="text-sm text-red-400">{error}</p>}
            {!error && !output && !loading && (
              <p className="text-sm italic text-slate-600">
                Your AI-written message will appear here…
              </p>
            )}
            {!error && (output || loading) && (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
                {output}
                {loading && <span className="pulse-dot ml-1 inline-block h-2 w-2 rounded-full bg-teal-400" />}
              </p>
            )}
          </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}
